/**
 * O tamanho da tela, para o layout que muda de FORMA (H15).
 *
 * ── Por que em JavaScript, se CSS resolve quase tudo ──────────────────────
 *
 * Resolve quase tudo, e o "quase" é esta task. Empilhar coluna, esconder
 * detalhe, mudar tipografia: tudo isso o CSS faz melhor, e é o que as telas já
 * fazem desde que nasceram.
 *
 * O que ele não faz é trocar TRÊS painéis por três etapas com navegação. Isso
 * exige saber qual etapa está aberta, e etapa é estado — não estilo. Fazer com
 * CSS exigiria renderizar os três e esconder dois, e aí o leitor de tela
 * anunciaria conteúdo que não está na tela, e o teclado percorreria campos
 * invisíveis.
 *
 * ── Por que a consulta é a MESMA do CSS ───────────────────────────────────
 *
 * O ponto de quebra vive em dois lugares por necessidade, e dois lugares
 * divergem. Manter o número aqui e importá-lo em nenhum outro seria pior; o que
 * dá para fazer é declará-lo uma vez neste arquivo e deixar escrito, no CSS,
 * que ele acompanha este valor.
 */

import { useEffect, useState } from 'react';

/**
 * 720px.
 *
 * Não é o tamanho de um aparelho — é onde três colunas param de caber com
 * texto legível. Abaixo disso a conversa fica com menos de 40 caracteres por
 * linha, que é onde a leitura passa a doer.
 */
export const LARGURA_ESTREITA = 720;

export const CONSULTA_ESTREITA = `(max-width: ${LARGURA_ESTREITA}px)`;

/**
 * `true` quando a tela é estreita o bastante para o layout mudar de forma.
 *
 * Assina a mudança em vez de ler uma vez: girar o aparelho e abrir o teclado
 * virtual mudam a largura, e um valor lido na montagem deixaria a tela no
 * formato errado até alguém recarregar.
 */
export function useTelaEstreita(): boolean {
  const [estreita, definirEstreita] = useState(() => consultar()?.matches ?? false);

  useEffect(() => {
    const consulta = consultar();
    if (!consulta) return;

    const aoMudar = (evento: MediaQueryListEvent): void => definirEstreita(evento.matches);
    consulta.addEventListener('change', aoMudar);

    // Sincroniza uma vez: entre a primeira renderização e este efeito a largura
    // pode ter mudado — acontece ao restaurar uma janela.
    definirEstreita(consulta.matches);

    return () => consulta.removeEventListener('change', aoMudar);
  }, []);

  return estreita;
}

/**
 * `matchMedia` pode não existir.
 *
 * Não é hipótese remota: o ambiente de teste em Node não tem, e o de jsdom tem
 * uma versão parcial. Devolver `null` faz o padrão ser "tela larga", que é o
 * layout completo — errar para o lado de mostrar mais é melhor que esconder
 * conteúdo por engano.
 */
function consultar(): MediaQueryList | null {
  if (typeof globalThis.matchMedia !== 'function') return null;
  return globalThis.matchMedia(CONSULTA_ESTREITA);
}
